/**
 * The one control every upload in the app opens: `openAssets({ fits, title, onPick, incoming })`. It shows the
 * library narrowed to what fits the place, takes uploads (button, drop or pasted files) and picks a finished upload
 * by itself. Mounted once, beside the toasts, as `<AssetsHost />`. A phone and an iPad get SeasonPicker.
 */
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLayout } from "../components/season/layout";
import { Icon } from "../components/ui/Icon";
import { useToast } from "../components/ui/Toast";
import { useFlashkutAccount } from "./flashkut";
import { ALL, type ListContext, fits, folderName, folderPath, kindName, lenLabel, listFiles, uploadTarget, usedLine, visibleAssets, visibleFolders } from "./library";
import { SeasonPicker } from "./SeasonPicker";
import { ByLine, Rail, RefRow, Results, SearchBox, Stage, Thumb, UpFoot, upSummary } from "./parts";
import { closePicker, createFolder, libraryBlind, loadLibrary, type PickerState, pickSelected, updatePicker, uploadFiles, uploadsAsAssets, useLibrary } from "./store";

export { openAssets } from "./store";

function DropOverlay({ path, count }: { path: string[]; count: number }) {
  return (
    <div className="as-drop in">
      <div className="as-drop-in">
        <span className="as-drop-ic">
          <Icon name="upload" />
        </span>
        <b>Drop to add to</b>
        <span className="as-drop-p">
          {path.map((n, i) => (
            <Fragment key={`${n}-${i}`}>
              {i ? <Icon name="chev" /> : null}
              <span>{n}</span>
            </Fragment>
          ))}
        </span>
        {count ? (
          <small>
            {count} file{count === 1 ? "" : "s"}
          </small>
        ) : null}
      </div>
    </div>
  );
}

const open = (id: string) => updatePicker({ folder: id, q: "", newFolder: false });
const isFiles = (e: React.DragEvent) => [...(e.dataTransfer?.types ?? [])].includes("Files");

function Picker({ p }: { p: PickerState }) {
  const lib = useLibrary();
  const toast = useToast();
  const flashkut = useFlashkutAccount();
  const folders = visibleFolders(lib.folders, flashkut);
  const shown = visibleAssets(lib.assets, flashkut);
  const blind = libraryBlind(lib);
  const search = useRef<HTMLInputElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const sheet = useRef<HTMLDivElement>(null);
  const [drop, setDrop] = useState<number | null>(null);
  const depth = useRef(0);

  const ctx: ListContext = { mode: "pick", folder: p.folder, q: p.q, fit: p.fit, all: p.all };
  const pending = lib.uploads.filter((u) => u.from === "pick");
  const all = [...uploadsAsAssets(pending), ...shown];
  const going = upSummary(pending, folderName(folders, pending[pending.length - 1]?.folderId ?? null));
  const first = new Set([...pending.map((u) => u.id), ...lib.fresh]);
  const files = listFiles(folders, all, ctx, first);
  const a = shown.find((x) => x.id === p.sel) ?? null;
  const ok = !!a && fits(ctx, a);
  const target = uploadTarget(folders, p.folder);

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    if (matchMedia("(pointer: fine)").matches) search.current?.focus({ preventScroll: true });
    return () => before?.focus?.({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing = !!t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName);
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        closePicker();
        return;
      }
      if (e.key === "/" && !typing) {
        e.preventDefault();
        search.current?.focus();
        return;
      }
      if (e.key === "Enter" && !typing && !t?.closest("button")) {
        e.preventDefault();
        pickSelected();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const upload = (list: File[]) => {
    if (!list.length || blind) return;
    if (p.q || (p.folder !== ALL && p.folder !== target)) updatePicker({ q: "", folder: target ?? ALL });
    for (const name of uploadFiles(list, target, "pick")) toast(`${name} is already going up.`);
  };

  return (
    <div
      className="as-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closePicker();
      }}
    >
      <div className="as-sheet" role="dialog" aria-modal="true" aria-label={p.title} ref={sheet}>
        <div className="as-pick">
          <header className="as-ph">
            <div className="sp">
              <h3>{p.title}</h3>
              {p.sub ? <p>{p.sub}</p> : null}
            </div>
            <button type="button" className="btn" disabled={blind} onClick={() => updatePicker({ newFolder: true, q: "" })}>
              <Icon name="foldplus" /> New folder
            </button>
            <button
              type="button"
              className={`btn p${going ? " as-upbusy" : ""}`}
              style={going ? ({ "--p": `${going.pct}%` } as React.CSSProperties) : undefined}
              disabled={blind}
              onClick={() => file.current?.click()}
            >
              <Icon name="upload" /> {going ? going.label : "Upload"}
            </button>
            <button type="button" className="ibtn" aria-label="Close" onClick={() => closePicker()}>
              <Icon name="x" />
            </button>
          </header>
          <div
            className={`as-pb${a ? " has-pane" : ""}`}
            onDragEnter={(e) => {
              if (!isFiles(e)) return;
              e.preventDefault();
              depth.current += 1;
              setDrop(e.dataTransfer.items?.length ?? 0);
            }}
            onDragOver={(e) => {
              if (isFiles(e)) e.preventDefault();
            }}
            onDragLeave={(e) => {
              if (!isFiles(e)) return;
              depth.current = Math.max(0, depth.current - 1);
              if (!depth.current) setDrop(null);
            }}
            onDrop={(e) => {
              if (!isFiles(e)) return;
              e.preventDefault();
              depth.current = 0;
              setDrop(null);
              upload([...(e.dataTransfer.files ?? [])]);
            }}
          >
            <Rail
              ctx={ctx}
              folders={folders}
              assets={all}
              blind={blind}
              search={<SearchBox id="as-q-pk" value={p.q} placeholder="Search Assets" inputRef={search} onChange={(q) => updatePicker({ q })} />}
              onOpen={open}
              onNewFolder={() => updatePicker({ newFolder: true, q: "" })}
            />
            <section className="as-pmain" aria-label="Files" id="as-main-pk">
              {lib.status === "loading" && !shown.length ? (
                <div className="as-empty">
                  <span className="spin" />
                </div>
              ) : lib.status === "error" ? (
                <div className="as-empty">
                  <Icon name="info" />
                  <b>{lib.error}</b>
                  <button type="button" className="btn s" onClick={() => void loadLibrary(true)}>
                    Try again
                  </button>
                </div>
              ) : (
                <Results
                  ctx={ctx}
                  folders={folders}
                  assets={all}
                  files={files}
                  uploads={pending}
                  sel={p.sel}
                  auto={p.auto}
                  fresh={lib.fresh}
                  height={132}
                  newFolder={p.newFolder}
                  newFolderWhere={folderName(folders, target)}
                  onSelect={(id) => updatePicker({ sel: id, auto: p.auto === id ? p.auto : null })}
                  onUse={() => pickSelected()}
                  onOpen={open}
                  onToggleAll={() => updatePicker({ all: !p.all })}
                  onClear={() => {
                    updatePicker({ q: "" });
                    search.current?.focus();
                  }}
                  onUpload={() => file.current?.click()}
                  onCreateFolder={(name) => {
                    void createFolder(name, target).then(
                      (f) => updatePicker({ newFolder: false, folder: f.id, sel: null }),
                      (e: unknown) => toast({ text: e instanceof Error ? e.message : "That folder wasn’t made.", error: true }),
                    );
                  }}
                  onCancelFolder={() => updatePicker({ newFolder: false })}
                />
              )}
            </section>
            {a ? (
              <aside className="as-pane" aria-label={a.name}>
                <Stage a={a} />
                <div className="as-pane-b">
                  <h4>{a.name}</h4>
                  <div className="as-kl">
                    {kindName(a)}
                    {a.duration_sec != null ? ` · ${lenLabel(a.duration_sec)}` : ""}
                    {a.pages ? ` · ${a.pages} page${a.pages === 1 ? "" : "s"}` : ""}
                  </div>
                  <ByLine a={a} fresh={lib.fresh.includes(a.id)} />
                  {ok ? null : (
                    <div className="note warn mt12">
                      <Icon name="info" />
                      <div>This doesn’t fit here. {p.fit.why ? p.fit.why(a) : p.fit.hint}</div>
                    </div>
                  )}
                  <div className="as-ul">{usedLine(a.uses)}</div>
                  {a.uses.slice(0, 3).map((u, i) => (
                    <RefRow key={`${u.kind}-${u.ref}-${i}`} u={u} />
                  ))}
                </div>
              </aside>
            ) : null}
            {drop !== null ? <DropOverlay path={["Assets", ...folderPath(folders, target).map((f) => f.name)]} count={drop} /> : null}
          </div>
          <footer className="as-pf">
            {a ? (
              <span className="as-pf-l">
                {p.auto === a.id ? (
                  <span className="pill good">
                    <Icon name="check" /> Just uploaded, picked for you
                  </span>
                ) : (
                  <>
                    <span className="as-pf-t">
                      <Thumb a={a} />
                    </span>
                    <span className="sp">
                      <b>{a.name}</b>
                      {p.error ? <span className="as-pf-bad" role="alert">{p.error}</span> : <span>{ok ? "Picked" : "Doesn’t fit here"}</span>}
                    </span>
                  </>
                )}
              </span>
            ) : going ? (
              <UpFoot s={going} />
            ) : (
              <span className="as-pf-l muted" />
            )}
            <span className="sp" />
            <button type="button" className="btn" onClick={() => closePicker()}>
              Cancel
            </button>
            <button type="button" className={`btn p${p.auto && p.auto === p.sel ? " as-pulse" : ""}`} disabled={!ok || !!p.using} onClick={() => pickSelected()}>
              {p.using ? (
                <>
                  <span className="spin" aria-hidden="true" /> Getting it ready
                </>
              ) : (
                <>
                  {p.cta}
                  <Icon name="right" />
                </>
              )}
            </button>
          </footer>
        </div>
        <input
          ref={file}
          type="file"
          hidden
          accept={p.all ? "" : p.fit.accept}
          onChange={(e) => {
            const list = [...(e.target.files ?? [])];
            e.target.value = "";
            upload(list);
          }}
        />
      </div>
    </div>
  );
}

/** Mounted once: draws the control while a place has it open. */
export function AssetsHost() {
  const { picker } = useLibrary();
  const layout = useLayout();
  if (!picker || typeof document === "undefined") return null;
  return createPortal(
    <div id="as-layer">{layout === "computer" ? <Picker key={picker.key} p={picker} /> : <SeasonPicker key={picker.key} p={picker} layout={layout} />}</div>,
    document.body,
  );
}

